import { useCallback, useEffect, useRef } from 'react';

const useDynamicVectorLayers = ({ mapRef }) => {
    const layersRef = useRef(new Map());

    const syncLayers = useCallback(
        (descriptors = []) => {
            const map = mapRef.current;
            if (!map) return;

            const nextIds = new Set();
            descriptors.forEach((descriptor) => {
                if (descriptor?.id) {
                    nextIds.add(descriptor.id);
                }
            });

            layersRef.current.forEach((layer, id) => {
                if (!nextIds.has(id)) {
                    map.removeLayer(layer);
                    layersRef.current.delete(id);
                }
            });

            descriptors.forEach((descriptor) => {
                if (!descriptor?.id || typeof descriptor.createLayer !== 'function') return;

                const existing = layersRef.current.get(descriptor.id);
                if (existing) {
                    map.removeLayer(existing);
                    layersRef.current.delete(descriptor.id);
                }

                const layer = descriptor.createLayer(map);
                if (!layer) return;

                if (descriptor.visible !== undefined) {
                    layer.setVisible(descriptor.visible);
                }
                if (descriptor.zIndex !== undefined) {
                    layer.setZIndex(descriptor.zIndex);
                }

                map.addLayer(layer);
                descriptor.onLayerReady?.(layer);
                layersRef.current.set(descriptor.id, layer);
            });
        },
        [mapRef]
    );

    useEffect(() => {
        return () => {
            const map = mapRef.current;
            if (!map) return;
            layersRef.current.forEach((layer) => map.removeLayer(layer));
            layersRef.current.clear();
        };
    }, [mapRef]);

    return { syncLayers };
};

export default useDynamicVectorLayers;
